import { on } from 'delegated-events';
import Pristine from 'pristinejs';
import scrollTo from './common/scrollTo';
import axios from 'axios';

const SELECTORS = Selectors({
  form: 'form',
  styledForm: 'form--styled',
  ajaxForm: 'form--ajax',
  formAnchor: '[data-form-anchor]',
  formScroll: '[data-form-scroll]',
  formErrorMessage: 'form-error-message',
  field: 'field',
  focused: 'is-focused',
  loading: 'is-loading',
});

export const pristineConfig = {
  classTo: 'field',
  errorClass: 'has-error',
  successClass: 'has-success',
  errorTextParent: 'field',
  errorTextTag: 'div',
  errorTextClass: 'text-help',
};

const fn = {
  init: () => {
    // Only setup any custom forms defined by our classes
    const form = document.querySelector(SELECTORS.form);
    fn.setupForm(form);

    on('change', '.has-field-error', e => {
      e.target.classList.remove('has-field-error');
    });
  },

  setupForm: form => {
    fn.setupFlyFields();
    fn.setupValidation(form);
  },

  setupValidation: form => {
    const pristine = new Pristine(form, pristineConfig);

    // Hide any native SilverStripe errors
    on('keyup', `${SELECTORS.asClass('styledForm')} input`, e => {
      const field = e.target.closest('.field');
      const error = field.querySelector('.message.required');

      if (error) {
        error.remove();
      }
    });

    form.addEventListener('submit', e => {
      if (e.target.skipValidation) {
        return true;
      }

      const valid = pristine.validate();

      if (!valid) {
        e.preventDefault();
        const firstError = form.querySelector('.field.has-error');

        if (firstError) {
          scrollTo(firstError, 50, () => firstError.querySelector('input').focus());
        }

        return false;
      }

      // Handle ajax submit if appropriate
      if (form.classList.contains(SELECTORS.ajaxForm)) {
        e.preventDefault();

        const errorMessage = form.querySelector(SELECTORS.asClass('formErrorMessage'));
        errorMessage.style.display = 'none';

        const submitButton = form.querySelector('[type=submit]');
        submitButton.disabled = true;
        submitButton.classList.add(SELECTORS.loading);
        const formData = new FormData(form);

        axios({
          method: 'post',
          url: form.action,
          data: formData,
          responseType: 'json',
          headers: {
            'content-type': 'multipart/form-data;',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json, text/javascript, */*',
          },
          timeout: 5000,
          responseEncoding: 'utf8',
        })
          .then(resp => {
            if (resp.data.success) {
              form.innerHTML = `<div class="form-success-message">${resp.data.message}</div>`;
              // Figure out where to scroll to
              let anchor = form.closest(SELECTORS.formAnchor);
              anchor =
                anchor ? anchor :
                form;
              scrollTo(anchor, 150);
              submitButton.disabled = false;
              submitButton.classList.remove(SELECTORS.loading);
            } else {
              errorMessage.innerHTML = resp.data.message;
              errorMessage.style.display = 'block';

              if (resp.field_errors) {
                for (const field of resp.field_errors) {
                  const errorField = form.querySelector(`[name="${field}"]`);
                  if (errorField) {
                    errorField.classList.add('has-field-error');
                  }
                }
              }
            }
          })
          .catch(err => {
            errorMessage.innerHTML = err;
            errorMessage.style.display = 'block';
            if (err.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              console.log(err.response.data);
              console.log(err.response.status);
              console.log(err.response.headers);
            } else if (err.request) {
              // The request was made but no response was received
              // `err.request` is an instance of XMLHttpRequest in the browser and an instance of
              // http.ClientRequest in node.js
              console.log(err.request);
            } else {
              // Something happened in setting up the request that triggered an err
              console.log('Error', err.message);
            }
            console.log(err.config);
          });
      }

      return valid;
    });
  },

  setupFlyFields: () => {
    on('focus', `${SELECTORS.asClass('styledForm')} input`, e => fn.handleFocus(e.target), {
      capture: true,
      passive: true,
    });
    on('blur', `${SELECTORS.asClass('styledForm')} input`, e => fn.handleBlur(e.target), {
      capture: true,
      passive: true,
    });
  },

  handleFocus: field => {
    const label = field.parentNode.parentNode.querySelector('label');
    if (label) {
      label.classList.add(SELECTORS.focused);
    }
  },

  handleBlur: field => {
    const label = field.parentNode.parentNode.querySelector('label');
    if (label && field.value === '') {
      label.classList.remove(SELECTORS.focused);
    }
  },
};

export default {
  can: () => document.body.classList.contains('page-home') || document.body.classList.contains('page-blog'),
  run: fn.init,
};
